import SettingsPageShell from '../../SettingsPageShell';
import { handleReactivate } from '../users-page-actions';
import { usersPageChrome } from '../users-page-chrome';
import type { UserRow } from '../userViewModel';
import type { useUsersPageData } from '../useUsersPageData';

import RdScopeBanner from './RdScopeBanner';
import UsersDialogs, { type ActiveDialog } from './UsersDialogs';
import UsersEmptyState from './UsersEmptyState';
import type { UsersFilterState } from './UsersFilterBar';
import UsersListPanel, { type UsersKpi } from './UsersListPanel';
import UsersPageHeaderActions from './UsersPageHeaderActions';

interface UsersPageBodyProps {
  data: ReturnType<typeof useUsersPageData>;
  filters: UsersFilterState;
  onFiltersChange: (next: UsersFilterState) => void;
  visibleRows: UserRow[];
  kpis: UsersKpi[];
  importedCount: number;
  selected: UserRow | null;
  onSelect: (row: UserRow) => void;
  onClearSelection: () => void;
  dialog: ActiveDialog;
  onDialog: (d: ActiveDialog) => void;
  onMutationSuccess: () => void;
  onResendInvite: (user: UserRow) => void;
}

/** The loaded Users page: shell, RD banner, list (or empty state) and the
 *  dialog the page currently wants open. */
export default function UsersPageBody({
  data,
  filters,
  onFiltersChange,
  visibleRows,
  kpis,
  importedCount,
  selected,
  onSelect,
  onClearSelection,
  dialog,
  onDialog,
  onMutationSuccess,
  onResendInvite,
}: UsersPageBodyProps) {
  const { crumb, inviteLabel, showEmpty, selfName } = usersPageChrome(data);

  return (
    <SettingsPageShell
      crumb={crumb}
      title="Users"
      actions={
        <UsersPageHeaderActions
          inviteLabel={inviteLabel}
          canInvite={data.userManagement.canInvite}
          onInvite={() => onDialog({ kind: 'invite' })}
        />
      }
    >
      {data.scopedRegionName && (
        <RdScopeBanner
          regionName={data.scopedRegionName}
          manageableCount={data.inScopeRows.filter((r) => r.allowedActions.length > 0).length}
        />
      )}
      {showEmpty ? (
        <UsersEmptyState selfName={selfName} selfEmail={data.self?.email ?? null} />
      ) : (
        <UsersListPanel
          kpis={kpis}
          rows={visibleRows}
          totalInScope={data.inScopeRows.length}
          selected={selected}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onSelect={onSelect}
          onClearSelection={onClearSelection}
          scopedRegionLabel={data.scopedRegionName}
          manageableRegionIds={data.manageableRegionIds}
          regionOptions={data.regionOptions}
          importedCount={importedCount}
          onDeactivate={(user) => onDialog({ kind: 'deactivate', user })}
          onReactivate={(user) => handleReactivate(user, onMutationSuccess)}
          onEdit={(user) => onDialog({ kind: 'edit', user })}
          onInvite={(user) => onDialog({ kind: 'invite', prefillFrom: user })}
          onResendInvite={onResendInvite}
          onRevokeInvite={(user) => onDialog({ kind: 'revoke-invite', user })}
        />
      )}
      <UsersDialogs
        dialog={dialog}
        onClose={() => onDialog(null)}
        onMutationSuccess={onMutationSuccess}
        existingUsers={data.allRows}
        selfUserId={data.self?.id ?? null}
        userManagement={data.userManagement}
        regionOptions={data.regionOptions}
      />
    </SettingsPageShell>
  );
}
